import React, { useState, useMemo, useEffect } from 'react';
import {
    Box, Button, Tooltip, Select, MenuItem, FormControl, InputLabel, Grid, Paper, Typography, ToggleButton, ToggleButtonGroup,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress, Divider, IconButton,
    Switch
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectProperties,
    selectGlobalProperty,
    selectLocalProperty,
    setLocalProperty
} from '../../redux/propertySlice';
import { GenerateBedCatCal } from '../../Api/PropertyMst/Api';
import axiosInstance from '../../lib/axios';
import OccupancyDetails from './OccupancyDetails';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import TotalInactiveBeds from './TotalInactiveBeds';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Tooltip as ChartTooltip,
    Legend,
    Filler
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Line, Doughnut, Bar, Pie } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement,
    ChartTooltip, Legend, Filler, ChartDataLabels
);

// ---- LIGHT: exactly this file's current values ----
const LIGHT_COLORS = {
    bg: '#f4f6fb',
    bgSoft: '#ffffff',
    card: '#ffffff',
    cardBorder: '#635BFF',
    borderLight: '#c3c9db',
    primary: '#3457d5',
    primaryDark: '#243e9e',
    primaryLight: '#635BFF',
    primaryGlow: '#635BFF',
    success: '#12946b',
    warning: '#b8720a',
    danger: '#d1343a',
    cyan: '#0a8fa0',
    pink: '#c22a72',
    text: '#161a26',
    textSecondary: '#454c62',
    textMuted: '#000'
};

// ---- DARK: original dark dashboard palette ----
const DARK_COLORS = {
    bg: '#0b0b14',
    bgSoft: '#100f1c',
    card: '#161528',
    cardBorder: '#2a2942',
    borderLight: '#3c3a5c',
    primary: '#635BFF',
    primaryDark: '#4f47e6',
    primaryLight: '#8f89ff',
    primaryGlow: 'rgba(99,91,255,0.35)',
    success: '#34d399',
    warning: '#fbbf24',
    danger: '#f87171',
    cyan: '#22d3ee',
    pink: '#e879f9',
    text: '#f6f6fb',
    textSecondary: '#b9b7e8',
    textMuted: '#8a87b3'
};

const LIGHT_KPI_ACCENTS = ['#3457d5', '#b8720a', '#c22a72', '#12946b', '#0a8fa0', '#7238c9'];
const DARK_KPI_ACCENTS = ['#635BFF', '#fbbf24', '#e879f9', '#34d399', '#22d3ee', '#f87171'];

export default function Occupancy() {
    const dispatch = useDispatch();
    const properties = useSelector(selectProperties);
    const globalProperty = useSelector(selectGlobalProperty);
    const localProperty = useSelector(state => selectLocalProperty(state, 'occupancy'));
    const selectedProperty = localProperty || globalProperty;
    const [dateFrom, setDateFrom] = useState(dayjs());
    const [dateTo, setDateTo] = useState(dayjs().add(11, 'months'));
    const [bedTypeFilter, setBedTypeFilter] = useState('all');
    const [trendMetric, setTrendMetric] = useState('occupancy');
    const [isLoading, setIsLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [apiData, setApiData] = useState([]);
    const [months, setMonths] = useState([]);
    const [matrixView, setMatrixView] = useState('summary');
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsFlags, setDetailsFlags] = useState([]);
    const [detailsTitle, setDetailsTitle] = useState('');
    const [isDarkMode, setIsDarkMode] = useState(false);
    const toggleTheme = () => setIsDarkMode(prev => !prev);
    const [openDialog, setOpenDialog] = useState(false);

    const COLORS = isDarkMode ? DARK_COLORS : LIGHT_COLORS;
    const KPI_ACCENTS = isDarkMode ? DARK_KPI_ACCENTS : LIGHT_KPI_ACCENTS;
    const GRADIENT_PRIMARY = `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`;
    const PALETTE = [COLORS.primary, COLORS.pink, COLORS.cyan, COLORS.warning];

    const cardSx = {
        background: isDarkMode ? `linear-gradient(180deg, ${COLORS.card} 0%, ${COLORS.bgSoft} 100%)` : COLORS.card,
        border: `1px solid ${COLORS.cardBorder}`,
        borderRadius: '12px',
        color: COLORS.text,
        height: '100%',
        boxShadow: isDarkMode ? 'none' : '0 1px 2px rgba(16,24,40,0.05), 0 1px 8px -4px rgba(16,24,40,0.06)',
        transition: 'transform 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease',
        '&:hover': {
            transform: 'translateY(-0.5px)',
            borderColor: isDarkMode ? COLORS.borderLight : COLORS.primaryLight,
            boxShadow: `0 8px 10px -10px ${COLORS.primaryGlow}`
        }
    };

    const headerSx = {
        px: 2.4, py: 1.5, borderBottom: `1px solid ${COLORS.cardBorder}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1
    };

    const darkFieldSx = {
        '& .MuiOutlinedInput-root': {
            color: COLORS.text,
            background: isDarkMode ? COLORS.bg : '#ffffff',
            borderRadius: '10px',
            '& fieldset': { borderColor: COLORS.borderLight },
            '&:hover fieldset': { borderColor: COLORS.primary },
            '&.Mui-focused fieldset': { borderColor: COLORS.primary }
        },
        '& .MuiInputLabel-root': { color: COLORS.textMuted },
        '& .MuiSvgIcon-root': { color: COLORS.textMuted }
    };

    const legendSx = { usePointStyle: true, padding: 12, color: COLORS.textSecondary, font: { size: 11 } };
    const tooltipTheme = { backgroundColor: COLORS.card, titleColor: COLORS.text, bodyColor: COLORS.textSecondary, borderColor: COLORS.cardBorder, borderWidth: 1, cornerRadius: 10, padding: 12 };

    const pctBadge = (n) => {
        if (isDarkMode) {
            return {
                display: 'inline-block', padding: '2px 8px', borderRadius: '999px', fontSize: '0.68rem', fontWeight: 700,
                background: n >= 50 ? 'rgba(248,113,113,0.2)' : n >= 20 ? 'rgba(251,191,36,0.2)' : 'rgba(52,211,153,0.2)',
                color: n >= 50 ? '#fca5a5' : n >= 20 ? '#fcd34d' : '#6ee7b7'
            };
        }
        return {
            display: 'inline-block', padding: '2px 8px', borderRadius: '999px', fontSize: '0.68rem', fontWeight: 700,
            background: n >= 50 ? 'rgba(209,52,58,0.12)' : n >= 20 ? 'rgba(184,114,10,0.14)' : 'rgba(18,148,107,0.12)',
            color: n >= 50 ? '#a8262b' : n >= 20 ? '#8f570a' : '#0c7a58'
        };
    };

    const heatCell = (pct) => {
        if (isDarkMode) {
            if (pct >= 50) return { bg: `rgba(248,113,113,${0.4 + pct / 180})`, text: '#fff' };
            if (pct >= 25) return { bg: `rgba(251,191,36,${0.35 + pct / 180})`, text: '#fff' };
            if (pct >= 5) return { bg: `rgba(52,211,153,${0.3 + pct / 140})`, text: '#fff' };
            return { bg: 'rgba(45,38,64,0.95)', text: '#fff' };
        }
        if (pct >= 50) return { bg: `rgba(209,42,49,${0.68 + pct / 300})`, text: '#ffffff' };
        if (pct >= 25) return { bg: `rgba(184,114,10,${0.62 + pct / 260})`, text: '#ffffff' };
        if (pct >= 5) return { bg: `rgba(16,148,107,${0.55 + pct / 220})`, text: '#ffffff' };
        return { bg: '#eef1f7', text: COLORS.textMuted };
    };

    const handleKpiClick = (card) => {
        if (!card.flags) return;
        setDetailsFlags(card.flags);
        setDetailsTitle(card.label);
        setDetailsOpen(true);
    };

    const handleDateFromChange = (newDate) => {
        if (newDate && newDate.isValid()) {
            const startDate = newDate.startOf('month');
            setDateFrom(startDate);
            setDateTo(startDate.add(11, 'months'));
        }
    };

    const handleDateToChange = (newDate) => {
        if (newDate && newDate.isValid()) {
            setDateTo(newDate.startOf('month'));
        }
    };

    const handleDropdownChange = (event) => {
        const selectedId = event.target.value;
        dispatch(setLocalProperty({ componentId: 'occupancy', propertyId: selectedId }));
    };

    useEffect(() => {
        if (selectedProperty) {
            fetchData();
        }
    }, [selectedProperty]);

    const fetchData = async () => {
        if (!selectedProperty) {
            toast.error('Please select a property');
            return;
        }

        const monthDiff = dateTo.diff(dateFrom, 'month');
        if (monthDiff < 0) {
            toast.warning("To date selection can't earlier than from date.");
            return;
        }
        if (monthDiff > 12) {
            toast.warning("Date Range Can't exceed 12 months.");
            return;
        }

        try {
            setIsLoading(true);

            const payload = {
                PropId: selectedProperty,
                BookDtFrom: dateFrom.format('YYYY-MM-DD'),
                CheckoutDt: dateTo.format('YYYY-MM-DD'),
                Flag: 'Summary'
            };

            const response = await GenerateBedCatCal(payload);

            if (response?.data?.Status === 0) {
                const data = response.data.Data?.BedCatCalList || [];
                setApiData(data);

                const labels = [];
                let current = dateFrom.startOf('month');
                while (current.isBefore(dateTo) || current.isSame(dateTo, 'month')) {
                    labels.push({ monthName: current.format('MMMM'), label: current.format('MMM-YY') });
                    current = current.add(1, 'month');
                }
                setMonths(labels);
            } else {
                toast.info(response?.data?.Message || 'Error fetching data.');
                setApiData([]);
                setMonths([]);
            }
        } catch (error) {
            toast.error('Failed to fetch data.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRefresh = async () => {
        if (!selectedProperty) {
            toast.error('Please select a property');
            return;
        }
        try {
            setIsRefreshing(true);
            const payload = { PropId: selectedProperty, Flag: '' };
            await Promise.all([
                axiosInstance.post('BedCalendar/InsertBedCat', payload),
                axiosInstance.post('BedCalendar/InsertBedCatCal', payload),
                axiosInstance.post('BedCalendar/UpdateBedCat', payload),
                axiosInstance.post('BedCalendar/UpdateBedCatCal_Data', payload),
            ]);
            toast.success('Latest data retrieved successfully.');
            await fetchData();
        } catch (error) {
            toast.error('Error while refreshing the data.');
        } finally {
            setIsRefreshing(false);
        }
    };

    const series = useMemo(() => {
        if (!apiData.length || !months.length) return [];

        const grouped = {};
        apiData.forEach(item => {
            const key = `${item.RoomTypeName}-${item.RoomBedTypeName}`;
            if (!grouped[key]) {
                grouped[key] = { roomType: item.RoomTypeName, bedType: item.RoomBedTypeName, byMonth: {} };
            }
            grouped[key].byMonth[item.MonthNm] = {
                total: (item.BedToSale || 0) + (item.ExtraBed || 0),
                booked: item.BookingBeds || 0,
                occupied: item.OccBeds || 0,
                balance: item.BalBed || 0,
                partial: item.PartialBeds || 0
            };
        });

        return Object.values(grouped).map(g => {
            const totalArr = [], bookedArr = [], occupiedArr = [], balanceArr = [], partialArr = [];
            months.forEach(m => {
                const rec = g.byMonth[m.monthName] || { total: 0, booked: 0, occupied: 0, balance: 0, partial: 0 };
                totalArr.push(rec.total);
                bookedArr.push(rec.booked);
                occupiedArr.push(rec.occupied);
                balanceArr.push(rec.balance);
                partialArr.push(rec.partial);
            });
            return { roomType: g.roomType, bedType: g.bedType, totalArr, bookedArr, occupiedArr, balanceArr, partialArr };
        });
    }, [apiData, months]);

    const bedTypeOptions = useMemo(() => Array.from(new Set(series.map(s => s.bedType))), [series]);

    const filteredSeries = useMemo(
        () => (bedTypeFilter === 'all' ? series : series.filter(s => s.bedType === bedTypeFilter)),
        [series, bedTypeFilter]
    );

    const totalsAt = (idx) => {
        let total = 0, occ = 0, booked = 0, bal = 0, partial = 0;
        filteredSeries.forEach(s => {
            total += s.totalArr[idx] || 0;
            occ += s.occupiedArr[idx] || 0;
            booked += s.bookedArr[idx] || 0;
            bal += s.balanceArr[idx] || 0;
            partial += s.partialArr[idx] || 0;
        });
        return {
            total, occ, booked, bal, partial,
            occPct: total ? (occ / total) * 100 : 0,
            pct: total ? ((occ + partial) / total) * 100 : 0,
            availPct: total ? (bal / total) * 100 : 0,
            bookedPct: total ? (booked / total) * 100 : 0,
            partialPct: total ? (partial / total) * 100 : 0
        };
    };

    const hasData = months.length > 0 && series.length > 0;
    const lastIdx = months.length ? months.length - 1 : 0;
    const lastTotals = hasData ? totalsAt(lastIdx) : { total: 0, occ: 0, booked: 0, bal: 0, partial: 0, occPct: 0, pct: 0, availPct: 0, bookedPct: 0, partialPct: 0 };
    const firstTotals = hasData ? totalsAt(0) : lastTotals;

    const { avgOcc, peakPct, peakMonth } = useMemo(() => {
        if (!hasData) return { avgOcc: 0, peakPct: 0, peakMonth: '' };
        let sumOccPartial = 0, sumTotal = 0, peak = 0, peakM = '';
        months.forEach((m, i) => {
            const t = totalsAt(i);
            sumOccPartial += t.occ + t.partial; sumTotal += t.total;
            if (t.pct > peak) { peak = t.pct; peakM = m.label; }
        });
        return { avgOcc: sumTotal ? (sumOccPartial / sumTotal) * 100 : 0, peakPct: peak, peakMonth: peakM };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [months, filteredSeries, hasData]);

    const trendChartData = useMemo(() => {
        if (!hasData) return null;
        const labels = months.map(m => m.label);
        const datasets = filteredSeries.map((s, idx) => {
            let data, label, isPct = true;
            if (trendMetric === 'occupancy') {
                data = s.occupiedArr.map((o, i) => (s.totalArr[i] ? +((o / s.totalArr[i]) * 100).toFixed(1) : 0));
                label = `${s.bedType} Occ %`;
            } else if (trendMetric === 'available') {
                data = s.balanceArr.map((b, i) => (s.totalArr[i] ? +((b / s.totalArr[i]) * 100).toFixed(1) : 0));
                label = `${s.bedType} Avail %`;
            } else if (trendMetric === 'bookedpct') {
                data = s.bookedArr.map((b, i) => (s.totalArr[i] ? +((b / s.totalArr[i]) * 100).toFixed(1) : 0));
                label = `${s.bedType} Booked %`;
            } else {
                data = s.occupiedArr; label = `${s.bedType} Occupied`; isPct = false;
            }
            const color = PALETTE[idx % PALETTE.length];
            return {
                label, data, borderColor: color, backgroundColor: `${color}28`, borderWidth: 2.5,
                tension: 0.4, pointRadius: 3, pointHoverRadius: 6, pointBackgroundColor: color, fill: true,
                datalabels: { display: true, color, font: { size: 10, weight: '700' }, align: 'top', offset: 4, formatter: v => (isPct ? `${v}%` : v) }
            };
        });
        return { labels, datasets };
    }, [filteredSeries, months, trendMetric, hasData, isDarkMode]);

    const trendOptions = {
        responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false },
        layout: { padding: { top: 18 } },
        plugins: { legend: { position: 'bottom', labels: legendSx }, tooltip: tooltipTheme, datalabels: { display: true } },
        scales: {
            y: { beginAtZero: true, grid: { color: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(16,24,40,0.08)' }, ticks: { color: COLORS.textMuted, callback: v => (trendMetric === 'occupied' ? v : `${v}%`) } },
            x: { grid: { display: false }, ticks: { color: COLORS.textMuted } }
        }
    };

    const donutData = useMemo(() => {
        if (!hasData) return null;
        const free = firstTotals.total - firstTotals.occ;
        return {
            labels: [`Occupied ${firstTotals.pct.toFixed(1)}%`, `Available ${firstTotals.availPct.toFixed(1)}%`, `Booked ${firstTotals.bookedPct.toFixed(1)}%`],
            datasets: [{ data: [firstTotals.occ, free, firstTotals.booked], backgroundColor: [COLORS.warning, COLORS.success, COLORS.pink], borderWidth: 0, hoverOffset: 8 }]
        };
    }, [firstTotals, hasData, isDarkMode]);

    const donutOptions = {
        responsive: true, maintainAspectRatio: false, cutout: '62%',
        plugins: {
            legend: { position: 'bottom', labels: legendSx },
            tooltip: { ...tooltipTheme, callbacks: { label: c => ` ${c.label.split(' ')[0]}: ${c.raw.toLocaleString()}` } },
            datalabels: { color: '#fff', font: { size: 11, weight: '700' }, formatter: (v) => (firstTotals.total ? (v / firstTotals.total) * 100 >= 5 ? `${((v / lastTotals.total) * 100).toFixed(0)}%` : '' : '') }
        }
    };

    const barOccupiedData = useMemo(() => {
        if (!hasData) return null;
        return {
            labels: months.map(m => m.label),
            datasets: filteredSeries.map((s, idx) => ({
                label: s.bedType, data: s.occupiedArr, backgroundColor: PALETTE[idx % PALETTE.length],
                borderRadius: 5, borderSkipped: false,
                datalabels: { display: true, color: '#fff', font: { size: 9, weight: '700' }, anchor: 'end', align: 'start', formatter: v => (v > 0 ? v : '') }
            }))
        };
    }, [filteredSeries, months, hasData, isDarkMode]);

    const barBookedAvailData = useMemo(() => {
        if (!hasData) return null;
        const booked = months.map((m, i) => totalsAt(i).booked);
        const avail = months.map((m, i) => totalsAt(i).bal);
        return {
            labels: months.map(m => m.label),
            datasets: [
                { label: 'Booked', data: booked, backgroundColor: COLORS.pink, borderRadius: 5, datalabels: { display: true, color: '#fff', font: { size: 9, weight: '700' }, anchor: 'end', align: 'start', formatter: v => (v > 0 ? v : '') } },
                { label: 'Available', data: avail, backgroundColor: COLORS.success, borderRadius: 5, datalabels: { display: true, color: '#fff', font: { size: 9, weight: '700' }, anchor: 'end', align: 'start', formatter: v => (v > 5 ? v : '') } }
            ]
        };
    }, [months, filteredSeries, hasData, isDarkMode]);

    const barOptions = {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom', labels: { ...legendSx, font: { size: 10 } } }, tooltip: tooltipTheme, datalabels: { display: true } },
        datasets: { bar: { categoryPercentage: 0.9, barPercentage: 0.9 } },
        scales: {
            y: { beginAtZero: true, grid: { color: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(16,24,40,0.08)' }, border: { color: COLORS.borderLight, display: true, drawTicks: true }, ticks: { color: COLORS.textMuted } },
            x: { grid: { display: false }, border: { color: COLORS.borderLight, display: true, drawTicks: true }, ticks: { color: COLORS.textMuted, font: { size: 10 } } }
        }
    };

    const pieBedTypeData = useMemo(() => {
        if (!hasData) return null;
        const byType = {};
        series.forEach(s => { byType[s.bedType] = (byType[s.bedType] || 0) + s.occupiedArr.reduce((a, b) => a + b, 0); });
        const labels = Object.keys(byType);
        const total = Object.values(byType).reduce((a, b) => a + b, 0) || 1;
        return {
            labels: labels.map(l => `${l} ${((byType[l] / total) * 100).toFixed(0)}%`),
            datasets: [{ data: labels.map(l => byType[l] || 0.01), backgroundColor: labels.map((_, i) => PALETTE[i % PALETTE.length]), borderWidth: 0 }]
        };
    }, [series, hasData, isDarkMode]);

    const pieOptions = {
        responsive: true, maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom', labels: { ...legendSx, font: { size: 10 } } },
            tooltip: { ...tooltipTheme, callbacks: { label: c => ` ${c.label.split(' ')[0]}: ${c.raw.toLocaleString()}` } },
            datalabels: { color: '#fff', font: { size: 12, weight: '700' } }
        }
    };

    const statusFor = (label, value, kind) => {
        let level = 'good';
        if (kind === 'occ') level = value >= 50 ? 'good' : value >= 20 ? 'warning' : 'critical';
        if (kind === 'avail') level = value > 70 ? 'critical' : value > 40 ? 'warning' : 'good';
        if (kind === 'booked') level = value > 15 ? 'good' : value > 5 ? 'warning' : 'critical';
        const colorMap = { good: COLORS.success, warning: COLORS.warning, critical: COLORS.danger };
        return { label, value, color: colorMap[level] };
    };

    return (
        <Box sx={{ background: COLORS.bg, minHeight: '100vh', maxWidth: '100vw', overflowX: 'hidden', color: COLORS.text, py: { xs: 1.5, md: 2 }, px: { xs: 1.5, md: 3 } }} >
            <ToastContainer />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.4, mb: 2.2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: '13px', background: GRADIENT_PRIMARY, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', boxShadow: `0 6px 18px -4px ${COLORS.primaryGlow}`, flexShrink: 0 }}>🛏️</Box>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2, fontSize: '1.05rem' }}>Inventory(Beds/Units) Analysis Dashboard</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: COLORS.textMuted, fontWeight: 500 }}>AI-assisted occupancy intelligence, live from your booking data</Typography>
                    </Box>
                </Box>

                <Tooltip title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'} arrow>
                    <Switch checked={isDarkMode} onChange={toggleTheme}
                        icon={
                            <Box
                                sx={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: COLORS.text,
                                }}
                            >
                                <DarkModeIcon
                                    sx={{
                                        fontSize: 15,
                                        color: COLORS.background,
                                    }}
                                />
                            </Box>
                        }
                        checkedIcon={
                            <Box
                                sx={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: COLORS.text,
                                }}
                            >
                                <LightModeIcon
                                    sx={{
                                        fontSize: 15,
                                        color: COLORS.background,
                                    }}
                                />
                            </Box>
                        }
                        sx={{
                            width: 58,
                            height: 30,
                            p: 0,

                            '& .MuiSwitch-switchBase': {
                                p: '4px',
                                transition: 'transform 0.3s ease',

                                '&.Mui-checked': {
                                    transform: 'translateX(26px)',
                                },
                            },

                            '& .MuiSwitch-track': {
                                borderRadius: 20,
                                backgroundColor: COLORS.borderLight,
                                opacity: 1,
                            },
                        }}
                    />
                </Tooltip>
            </Box>

            {/* -------- Filter bar: reused from BedCategorySummary -------- */}
            <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
                    <FormControl sx={{ width: { xs: 300, md: 250 }, ...darkFieldSx }}>
                        <InputLabel id="property-select-label">Property</InputLabel>
                        <Select
                            labelId="property-select-label"
                            value={selectedProperty || ''}
                            onChange={handleDropdownChange}
                            label="Property"
                            sx={{ height: '42px' }}
                        >
                            {properties?.map((item) => (
                                <MenuItem key={item.Id} value={item.Id}>{item.Name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                            <DatePicker
                                label="From-Date"
                                value={dateFrom}
                                onChange={handleDateFromChange}
                                format="MM/YYYY"
                                views={['month', 'year']}
                                sx={{ width: 150, ...darkFieldSx }}
                            />
                            <DatePicker
                                label="To-Date"
                                value={dateTo}
                                onChange={handleDateToChange}
                                format="MM/YYYY"
                                views={['month', 'year']}
                                minDate={dateFrom}
                                maxDate={dateFrom ? dateFrom.add(12, 'months') : null}
                                sx={{ width: 150, ...darkFieldSx }}
                            />
                        </Box>
                    </LocalizationProvider>

                    <Tooltip title="Click to see data" arrow>
                        <span>
                            <Button variant="contained"
                                onClick={fetchData}
                                disabled={isLoading || isRefreshing}
                                sx={{ height: '42px', minWidth: 118, whiteSpace: 'nowrap', background: GRADIENT_PRIMARY, borderRadius: '10px', textTransform: 'none', fontWeight: 700, boxShadow: `0 4px 16px -4px ${COLORS.primaryGlow}`, '&:hover': { background: GRADIENT_PRIMARY, boxShadow: `0 6px 22px -4px ${COLORS.primaryGlow}` }, '&.Mui-disabled': { background: isDarkMode ? 'rgba(99,91,255,0.25)' : 'rgba(52,87,213,0.2)', color: 'rgba(255,255,255,0.6)' } }}
                            >
                                {isLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Get Data'}
                            </Button>
                        </span>
                    </Tooltip>

                    <Tooltip title="Refresh latest data" arrow>
                        <span>
                            <Button
                                variant="outlined"
                                onClick={handleRefresh}
                                disabled={isLoading || isRefreshing}
                                startIcon={isRefreshing ? <CircularProgress size={16} sx={{ color: COLORS.primaryLight }} /> : null}
                                sx={{ height: '42px', minWidth: 118, whiteSpace: 'nowrap', borderColor: COLORS.borderLight, color: COLORS.textSecondary, borderRadius: '10px', textTransform: 'none', fontWeight: 700, '&:hover': { borderColor: COLORS.primary, background: isDarkMode ? 'rgba(99,91,255,0.08)' : 'rgba(52,87,213,0.06)', color: COLORS.text }, '&.Mui-disabled': { borderColor: COLORS.cardBorder, color: COLORS.textMuted } }}
                            >
                                {isRefreshing ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </span>
                    </Tooltip>

                    <Tooltip title="Check InActive Beds" arrow>
                        <span>
                            <Button variant="outlined"
                                onClick={() => setOpenDialog(true)}
                                // startIcon={isRefreshing ? (<CircularProgress size={16} sx={{ color: '#D97706' }} />) : null}
                                disabled={isLoading || isRefreshing}
                                sx={{
                                    height: '42px',
                                    minWidth: 118,
                                    whiteSpace: 'nowrap',
                                    borderColor: '#D97706',
                                    color: '#D97706',
                                    borderRadius: '10px',
                                    textTransform: 'none',
                                    fontWeight: 700,

                                    '&:hover': {
                                        borderColor: '#B45309',
                                        background: 'rgba(217, 119, 6, 0.08)',
                                        color: '#B45309',
                                    },

                                    '&.Mui-disabled': {
                                        borderColor: 'rgba(217, 119, 6, 0.25)',
                                        color: 'rgba(217, 119, 6, 0.45)',
                                    },
                                }}
                            >
                                Inactive Beds
                            </Button>
                        </span>
                    </Tooltip>

                    {bedTypeOptions.length > 1 && (
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={bedTypeFilter}
                            onChange={(e, v) => v && setBedTypeFilter(v)}
                            sx={{ ml: 'auto', background: COLORS.bg, borderRadius: '8px', p: '3px' }}
                        >
                            <ToggleButton value="all" sx={{ color: COLORS.textMuted, textTransform: 'none', border: 0, borderRadius: '6px !important', px: 1.4, '&.Mui-selected': { background: COLORS.primary, color: '#fff' } }}>All Beds</ToggleButton>
                            {bedTypeOptions.map(bt => (
                                <ToggleButton key={bt} value={bt} sx={{ color: COLORS.textMuted, textTransform: 'none', border: 0, borderRadius: '6px !important', px: 1.4, '&.Mui-selected': { background: COLORS.primary, color: '#fff' } }}>{bt}</ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    )}
                </Box>
            </Paper>

            {!hasData ? (
                <Paper elevation={0} sx={{ ...cardSx, p: 6, textAlign: 'center' }}>
                    {(isLoading || isRefreshing) ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.6 }}>
                            <CircularProgress size={34} sx={{ color: COLORS.primary }} />
                            <Typography sx={{ color: COLORS.textSecondary, fontWeight: 600 }}>
                                {isRefreshing ? 'Refreshing the latest booking data…' : 'Loading occupancy data…'}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ fontSize: '2rem' }}>📊</Box>
                            <Typography sx={{ color: COLORS.text, fontWeight: 700 }}>No data loaded yet</Typography>
                            <Typography sx={{ color: COLORS.textMuted, fontSize: '0.85rem', maxWidth: 420 }}>
                                Select a property and date range above, then click <b>Get Data</b> to build the occupancy dashboard.
                            </Typography>
                        </Box>
                    )}
                </Paper>
            ) : (
                <>
                    {(isLoading || isRefreshing) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, px: 1.6, py: 1, borderRadius: '10px', background: isDarkMode ? 'rgba(99,91,255,0.08)' : 'rgba(52,87,213,0.06)', border: `1px solid ${COLORS.primaryGlow}` }}>
                            <CircularProgress size={16} sx={{ color: COLORS.primary }} />
                            <Typography sx={{ fontSize: '0.78rem', color: COLORS.primaryLight, fontWeight: 600 }}>
                                {isRefreshing ? 'Refreshing latest booking data…' : 'Loading…'}
                            </Typography>
                        </Box>
                    )}

                    {/* -------- AI recommendation banner -------- */}
                    <Paper elevation={0} sx={{ background: isDarkMode ? `linear-gradient(135deg, rgba(99,91,255,0.16), rgba(232,121,249,0.08))` : `linear-gradient(135deg, rgba(52,87,213,0.09), rgba(194,42,114,0.06))`, border: `1px solid ${COLORS.primaryGlow}`, borderRadius: '10px', px: 2, py: 1, mb: 1.5, position: 'relative', overflow: 'hidden' }}>
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',

                            '@media (max-width:600px)': {
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 0.5,
                            },
                        }}
                        >
                            <Typography sx={{ fontWeight: 700, color: COLORS.primaryLight, display: 'flex', alignItems: 'center', gap: 0.6, whiteSpace: 'nowrap' }}>
                                🧠 AI Recommendation →
                            </Typography>

                            <Typography sx={{ fontSize: '0.85rem', color: COLORS.textSecondary, lineHeight: 1.6, flex: 1 }}>
                                Occupancy moves by{' '}
                                <b>{(lastTotals.pct - firstTotals.pct).toFixed(1)} points</b> from{' '}
                                {months[0]?.label} to {months[lastIdx]?.label}. Available inventory sits at{' '}
                                <b>{lastTotals.availPct.toFixed(0)}%</b> while booked pipeline is{' '}
                                <b>{lastTotals.bookedPct.toFixed(1)}%</b> in the final month —{' '}
                                {lastTotals.bal.toLocaleString()} beds remain free to sell.
                            </Typography>
                        </Box>
                    </Paper>

                    {/* -------- KPI row -------- */}
                    <Grid container spacing={1.8} sx={{ mb: 1.5 }}>
                        {[
                            { label: 'Total Inventory', value: firstTotals.total.toLocaleString(), meta: 'Beds', icon: '🛏️', flags: ['O', 'B', 'A', 'P'] },
                            {
                                label: 'Occupied / Partial',
                                value: `${firstTotals.occ.toLocaleString()} / ${firstTotals.partial.toLocaleString()}`,
                                meta: `${firstTotals.pct.toFixed(1)}% Occ / • ${firstTotals.partialPct.toFixed(1)}% Partial • ${months[0]?.label}`,
                                icon: '✅',
                                flags: ['O', 'P']
                            },
                            { label: 'Booked', value: firstTotals.booked.toLocaleString(), meta: `${firstTotals.bookedPct.toFixed(1)}% of inventory`, icon: '📌', flags: ['B'] },
                            { label: 'Available', value: firstTotals.bal.toLocaleString(), meta: `${firstTotals.availPct.toFixed(1)}% free`, icon: '🟢', flags: ['A'] },
                            { label: 'Occupancy', value: `${firstTotals.pct.toFixed(2)}%`, meta: `${months[0]?.label} snapshot`, icon: '📊' },
                            { label: 'Peak Occupancy', value: `${peakPct.toFixed(2)}%`, meta: peakMonth, icon: '🔥' },
                        ].map((k, i) => (
                            <Grid item xs={6} md={2} key={i}>
                                <Paper elevation={0}
                                    onClick={() => handleKpiClick(k)}
                                    sx={{ ...cardSx, p: 1.5, position: 'relative', overflow: 'hidden', cursor: k.flags ? 'pointer' : 'default' }}
                                >
                                    <Box sx={{ position: 'absolute', top: -18, right: -18, width: 64, height: 64, borderRadius: '50%', background: KPI_ACCENTS[i], opacity: 0.14 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.6 }}>
                                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: COLORS.textMuted }}>{k.label}</Typography>
                                        <Box sx={{ fontSize: '0.95rem' }}>{k.icon}</Box>
                                    </Box>
                                    <Typography sx={{ fontSize: '1.55rem', fontWeight: 800, letterSpacing: '-0.03em' }}>{k.value}</Typography>
                                    <Typography sx={{ fontSize: '0.7rem', color: COLORS.textMuted, mt: 0.4 }}>{k.meta}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>

                    <Grid container spacing={1.8} sx={{ mb: 1.5 }}>
                        <Grid item xs={12} md={8}>
                            <Paper elevation={0} sx={cardSx}>
                                <Box sx={headerSx}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Occupancy Heatmap</Typography>
                                </Box>

                                <Box sx={{ p: 2, overflowX: 'auto' }}>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: `110px repeat(${months.length}, minmax(60px, 80px))`,
                                            gap: '3px',
                                            width: 'fit-content',
                                            maxWidth: '100%',
                                        }}
                                    >
                                        <Box />

                                        {months.map((m) => (
                                            <Typography key={m.label} sx={{ fontSize: '0.72rem', fontWeight: 600, color: COLORS.textMuted, textAlign: 'center', pb: 0.4 }}>
                                                {m.label}
                                            </Typography>
                                        ))}

                                        {filteredSeries.map((s) => (
                                            <React.Fragment key={`${s.roomType}-${s.bedType}`}>
                                                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: COLORS.textSecondary, display: 'flex', alignItems: 'center' }}>
                                                    {s.bedType}
                                                </Typography>

                                                {s.occupiedArr.map((o, i) => {
                                                    const total = Number(s.totalArr[i] || 0);
                                                    const partial = Number(s.partialArr?.[i] || 0);
                                                    const occupancy = o + partial;
                                                    const pct = total ? (occupancy / total) * 100 : 0;
                                                    const cell = heatCell(pct);

                                                    return (
                                                        <Box key={`${s.roomType}-${s.bedType}-${i}`}
                                                            title={`${months[i].label}: ${o} occupied + ${partial} partial (${pct.toFixed(1)}%)`}
                                                            sx={{
                                                                background: cell.bg,
                                                                borderRadius: '6px',
                                                                aspectRatio: '1.35',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: cell.text,
                                                                border: pct < 5 ? `1px solid ${COLORS.cardBorder}` : 'none',
                                                                lineHeight: 1.1,
                                                            }}
                                                        >
                                                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>
                                                                {occupancy % 1 === 0 ? occupancy : occupancy.toFixed(1)}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '0.62rem', fontWeight: 500, opacity: 0.9, mt: 0.3 }}>
                                                                {pct.toFixed(0)}%
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}
                                            </React.Fragment>
                                        ))}

                                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: COLORS.text, display: 'flex', alignItems: 'center' }}>
                                            Total
                                        </Typography>

                                        {months.map((m, i) => {
                                            const totalOccupied = filteredSeries.reduce((sum, s) => sum + Number(s.occupiedArr[i] || 0), 0);
                                            const totalPartial = filteredSeries.reduce((sum, s) => sum + Number(s.partialArr?.[i] || 0), 0);
                                            const totalBeds = filteredSeries.reduce((sum, s) => sum + Number(s.totalArr[i] || 0), 0);
                                            const totalOccupancy = totalOccupied + totalPartial;
                                            const pct = totalBeds ? (totalOccupancy / totalBeds) * 100 : 0;
                                            const cell = heatCell(pct);

                                            return (
                                                <Box
                                                    key={`total-${i}`}
                                                    title={`${m.label}: ${totalOccupied} occupied + ${totalPartial} partial (${pct.toFixed(1)}%)`}
                                                    sx={{
                                                        background: cell.bg,
                                                        borderRadius: '6px',
                                                        aspectRatio: '1.35',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: cell.text,
                                                        border: `1.5px solid ${COLORS.primary}`,
                                                        lineHeight: 1.1,
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                                                        {totalOccupancy % 1 === 0 ? totalOccupancy : totalOccupancy.toFixed(1)}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 500, opacity: 0.9, mt: 0.3 }}>
                                                        {pct.toFixed(0)}%
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Paper elevation={0} sx={cardSx}>
                                <Box sx={headerSx}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Bed Status Wise %</Typography>
                                    <Typography sx={{ fontSize: '0.68rem', color: COLORS.textMuted }}>Occupied • Available • Booked</Typography>
                                </Box>

                                <Box sx={{ p: 2, height: 260 }}>
                                    {donutData && <Doughnut data={donutData} options={donutOptions} />}
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* -------- Trend + Donut -------- */}
                    <Grid container spacing={1.8} sx={{ mb: 1.8 }}>
                        <Grid item xs={12} md={12}>
                            <Paper elevation={0} sx={cardSx}>
                                <Box sx={headerSx}>
                                    <Box>
                                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Occupancy & Availability Trend</Typography>
                                        <Typography sx={{ fontSize: '0.68rem', color: COLORS.textMuted }}>Toggle metric to explore the range</Typography>
                                    </Box>
                                    <ToggleButtonGroup size="small" exclusive value={trendMetric} onChange={(e, v) => v && setTrendMetric(v)} sx={{ background: COLORS.bg, borderRadius: '8px', p: '3px' }}>
                                        {[['occupancy', 'Occ %'], ['available', 'Avail %'], ['bookedpct', 'Booked %'], ['occupied', 'Count']].map(([val, lbl]) => (
                                            <ToggleButton key={val} value={val} sx={{ color: COLORS.textMuted, textTransform: 'none', border: 0, borderRadius: '6px !important', px: 1.2, fontSize: '0.7rem', '&.Mui-selected': { background: COLORS.primary, color: '#fff' } }}>{lbl}</ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </Box>
                                <Box sx={{ p: 2, height: 300 }}>
                                    {trendChartData && <Line data={trendChartData} options={trendOptions} />}
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
                        <Box
                            sx={{
                                ...headerSx,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Full Availability Matrix</Typography>

                                <ToggleButtonGroup
                                    value={matrixView}
                                    exclusive
                                    onChange={(e, value) => value && setMatrixView(value)}
                                    size="small"
                                    sx={{
                                        background: COLORS.bg,
                                        border: `1px solid ${COLORS.borderLight}`,
                                        borderRadius: '9px',
                                        p: '3px',
                                        '& .MuiToggleButton-root': {
                                            border: 0,
                                            borderRadius: '7px !important',
                                            px: 1.8,
                                            py: 0.55,
                                            minWidth: 80,
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            color: COLORS.textMuted,
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                background: isDarkMode ? 'rgba(99,91,255,0.08)' : 'rgba(52,87,213,0.06)',
                                                color: COLORS.primary,
                                            },
                                            '&.Mui-selected': {
                                                background: COLORS.primary,
                                                color: '#fff',
                                                boxShadow: `0 2px 8px ${COLORS.primaryGlow}`,
                                                '&:hover': { background: COLORS.primary },
                                            },
                                        },
                                    }}
                                >
                                    <ToggleButton value="summary">Summary</ToggleButton>
                                    <ToggleButton value="details"> Details</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            <Typography sx={{ fontSize: '0.68rem', color: COLORS.textMuted, whiteSpace: 'nowrap' }}>
                                {months[0]?.label} → {months[lastIdx]?.label}
                            </Typography>
                        </Box>

                        {matrixView === 'summary' && (
                            <TableContainer sx={{ maxHeight: 440 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            {['Room', 'Metric', ...months.map(m => m.label)].map((h, i) => (
                                                <TableCell
                                                    key={i}
                                                    sx={{
                                                        background: isDarkMode ? '#1c1a30' : '#eef1f8',
                                                        color: COLORS.textMuted,
                                                        fontWeight: 700,
                                                        fontSize: '0.65rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.03em',
                                                        borderBottom: `1px solid ${COLORS.cardBorder}`,
                                                        textAlign: i === 0 ? 'left' : 'center'
                                                    }}
                                                >
                                                    {h}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {(() => {
                                            const summary = { total: [], booked: [], occupied: [], balance: [], partial: [] };

                                            months.forEach((_, i) => {
                                                summary.total[i] = filteredSeries.reduce((sum, s) => sum + Number(s.totalArr?.[i] || 0), 0);
                                                summary.booked[i] = filteredSeries.reduce((sum, s) => sum + Number(s.bookedArr?.[i] || 0), 0);
                                                summary.occupied[i] = filteredSeries.reduce((sum, s) => sum + Number(s.occupiedArr?.[i] || 0), 0);
                                                summary.balance[i] = filteredSeries.reduce((sum, s) => sum + Number(s.balanceArr?.[i] || 0), 0);
                                                summary.partial[i] = filteredSeries.reduce((sum, s) => sum + Number(s.partialArr?.[i] || 0), 0);
                                            });

                                            const metrics = [
                                                { key: 'Total Bed', vals: summary.total, format: v => Number(v).toLocaleString() },
                                                { key: 'Booked', vals: summary.booked, format: (v, i) => { const total = summary.total[i]; const pct = total ? (v / total) * 100 : 0; return `${v.toLocaleString()} (${pct.toFixed(1)}%)`; } },
                                                { key: 'Occupied', vals: summary.occupied, format: (v, i) => { const partial = summary.partial[i]; const total = summary.total[i]; const occupancy = v + partial; const pct = total ? (occupancy / total) * 100 : 0; return `${occupancy.toLocaleString(undefined, { maximumFractionDigits: 1 })} (${pct.toFixed(1)}%)`; } },
                                                { key: 'Balance', vals: summary.balance, format: (v, i) => { const total = summary.total[i]; const pct = total ? (v / total) * 100 : 0; return `${v.toLocaleString()} (${pct.toFixed(1)}%)`; } },
                                                { key: 'Partial', vals: summary.partial, format: v => v.toLocaleString(undefined, { maximumFractionDigits: 1 }) }
                                            ];

                                            return metrics.map((m, mi) => (
                                                <TableRow key={m.key} sx={{ '& td': { borderBottom: `1px solid ${COLORS.cardBorder}`, color: COLORS.textMuted, fontSize: '0.74rem' } }}>
                                                    {mi === 0 && (
                                                        <TableCell rowSpan={metrics.length} sx={{ verticalAlign: 'middle', fontWeight: 700, color: `${COLORS.text} !important` }}>
                                                            All Rooms
                                                        </TableCell>
                                                    )}
                                                    <TableCell sx={{ textAlign: 'left', fontWeight: 600, color: `${COLORS.text} !important` }}>{m.key}</TableCell>
                                                    {m.vals.map((v, i) => (
                                                        <TableCell key={i} sx={{ textAlign: 'center', fontWeight: m.key === 'Total Bed' ? 700 : 500 }}>
                                                            {m.format(v, i)}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ));
                                        })()}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {matrixView === 'details' && (
                            <TableContainer sx={{ maxHeight: 440 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            {['Room', 'Bed', 'Metric', ...months.map(m => m.label)].map((h, i) => (
                                                <TableCell key={i} sx={{ background: isDarkMode ? '#1c1a30' : '#eef1f8', color: COLORS.textMuted, fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: `1px solid ${COLORS.cardBorder}`, textAlign: i < 3 ? 'left' : 'center' }}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredSeries.map((s) => {
                                            const metrics = [
                                                { key: 'Total Bed', vals: s.totalArr, pct: false },
                                                { key: 'Booked', vals: s.bookedArr, pct: false },
                                                { key: 'Occupied', vals: s.occupiedArr, pct: false },
                                                { key: 'Balance', vals: s.balanceArr, pct: false },
                                                { key: 'Partial', vals: s.partialArr, pct: false },
                                                { key: 'Occupancy %', vals: s.occupiedArr.map((o, i) => (s.totalArr[i] ? (o / s.totalArr[i]) * 100 : 0)), pct: true },
                                                { key: 'Booked %', vals: s.bookedArr.map((b, i) => (s.totalArr[i] ? (b / s.totalArr[i]) * 100 : 0)), pct: true },
                                                { key: 'Available %', vals: s.balanceArr.map((b, i) => (s.totalArr[i] ? (b / s.totalArr[i]) * 100 : 0)), pct: true }
                                            ];
                                            return metrics.map((m, mi) => (
                                                <TableRow key={`${s.roomType}-${s.bedType}-${m.key}`} sx={{ '& td': { borderBottom: `1px solid ${COLORS.cardBorder}`, color: COLORS.textMuted, fontSize: '0.74rem' } }}>
                                                    {mi === 0 && (
                                                        <>
                                                            <TableCell rowSpan={metrics.length} sx={{ verticalAlign: 'middle', fontWeight: 600, color: `${COLORS.text} !important` }}>{s.roomType}</TableCell>
                                                            <TableCell rowSpan={metrics.length} sx={{ verticalAlign: 'middle', fontWeight: 600, color: `${COLORS.text} !important` }}>{s.bedType}</TableCell>
                                                        </>
                                                    )}
                                                    <TableCell sx={{ textAlign: 'left', fontWeight: 500, color: `${COLORS.text} !important` }}>{m.key}</TableCell>
                                                    {m.vals.map((v, i) => (
                                                        <TableCell key={i} sx={{ textAlign: 'center' }}>
                                                            {m.pct ? <Box component="span" sx={pctBadge(v)}>{v.toFixed(1)}%</Box> : Math.round(v).toLocaleString()}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ));
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>

                    {/* -------- Three bar/pie charts -------- */}
                    <Grid container spacing={1.8} sx={{ mb: 1.8 }}>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={0} sx={cardSx}>
                                <Box sx={headerSx}><Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Occupied Beds</Typography></Box>
                                <Box sx={{ p: 1.5, height: 230 }}>{barOccupiedData && <Bar data={barOccupiedData} options={barOptions} />}</Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={0} sx={cardSx}>
                                <Box sx={headerSx}><Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Booked vs Available</Typography></Box>
                                <Box sx={{ p: 1.5, height: 230 }}>{barBookedAvailData && <Bar data={barBookedAvailData} options={barOptions} />}</Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* -------- Health status -------- */}
                    <Grid container spacing={1.8} sx={{ mb: 1.8 }}>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={0} sx={cardSx}>
                                <Box sx={headerSx}><Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Health Status</Typography></Box>
                                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.4 }}>
                                    {[
                                        statusFor('Occupancy', lastTotals.pct, 'occ'),
                                        statusFor('Available %', lastTotals.availPct, 'avail'),
                                        statusFor('Booked %', lastTotals.bookedPct, 'booked')
                                    ].map((row, i) => (
                                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{row.label}</Typography>
                                            <Chip size="small" label={`${row.value.toFixed(1)}%`} sx={{ background: `${row.color}26`, color: row.color, fontWeight: 700 }} />
                                        </Box>
                                    ))}
                                    <Divider sx={{ borderColor: COLORS.cardBorder }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Trend</Typography>
                                        <Chip size="small" label={lastTotals.pct < firstTotals.pct ? 'Declining' : 'Stable / Up'} sx={{ background: lastTotals.pct < firstTotals.pct ? `${COLORS.danger}26` : `${COLORS.success}26`, color: lastTotals.pct < firstTotals.pct ? COLORS.danger : COLORS.success, fontWeight: 700 }} />
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={0} sx={cardSx}>
                                <Box sx={{ p: 0, height: 270 }}>
                                    <Box sx={headerSx}><Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Bed Type Share</Typography></Box>
                                    <Box sx={{ p: 1.5, height: 230 }}>{pieBedTypeData && <Pie data={pieBedTypeData} options={pieOptions} />}</Box>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Typography sx={{ textAlign: 'center', fontSize: '0.7rem', color: COLORS.textMuted, mt: 2.5, pt: 1.2, borderTop: `1px solid ${COLORS.cardBorder}` }}>
                        Occupancy % = Occupied ÷ Total • Available % = Balance ÷ Total • Booked % = Booked ÷ Total
                    </Typography>
                </>
            )}

            <OccupancyDetails
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                flags={detailsFlags}
                title={detailsTitle}
                propId={selectedProperty}
                fromMonthId={dateFrom.month() + 1}
                fromMonthName={dateFrom.format('MMMM')}
                fromYear={dateFrom.year()}
            />

            <TotalInactiveBeds open={openDialog} onClose={() => setOpenDialog(false)} propId={selectedProperty} />
        </Box>
    );
}